// Historial.js — Panel de superadmin (Importar Datos > Historial).
// Consulta cruzada de sync_history (altas/bajas/modificaciones de pisos y
// alumnos) con filtros estilo Excel por columna, rango de fechas y orden.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import PanelFlotante from "../../components/PanelFlotante";

// ─────────────────────────────────────────────
// Popover de filtro tipo Excel: lista de valores únicos con checkboxes + buscador
// ─────────────────────────────────────────────
const ColumnFilterPopover = ({ opciones, seleccionados, onChange }) => {
  const [abierto, setAbierto] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [temp, setTemp] = useState(seleccionados);
  const ref = useRef(null);

  useEffect(() => { if (abierto) setTemp(seleccionados); }, [abierto, seleccionados]);

  const cerrar = useCallback(() => setAbierto(false), []);

  const filtradas = busqueda.trim()
    ? opciones.filter((o) => String(o).toLowerCase().includes(busqueda.trim().toLowerCase()))
    : opciones;

  const toggle = (v) => setTemp((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));
  const activo = seleccionados.length > 0;

  return (
    <span ref={ref} style={{ position: "relative", display: "inline-block", marginLeft: 5 }}>
      <button
        onClick={(e) => { e.stopPropagation(); setAbierto((a) => !a); }}
        title="Filtrar"
        style={{
          border: "none", background: activo ? "#4338ca" : "rgba(255,255,255,0.15)",
          color: activo ? "#fff" : "#cbd5e1", borderRadius: 5, cursor: "pointer",
          fontSize: 10, padding: "2px 6px", lineHeight: 1.4, fontWeight: 700,
        }}
      >▾</button>
      {abierto && (
        <PanelFlotante anchorRef={ref} onCerrar={cerrar} style={{
          background: "#fff",
          border: "1.5px solid #e2e8f0", borderRadius: 12, boxShadow: "0 16px 36px rgba(15,23,42,0.25)",
          width: 230, padding: 10, textAlign: "left", color: "#334155",
        }}>
          <input
            value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="🔍 Buscar…"
            style={{ width: "100%", padding: "6px 8px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12.5, marginBottom: 6, boxSizing: "border-box", outline: "none" }}
          />
          <div style={{ display: "flex", gap: 10, marginBottom: 6, fontSize: 11 }}>
            <span onClick={() => setTemp(filtradas)} style={{ color: "#4338ca", cursor: "pointer", fontWeight: 700 }}>Marcar todos</span>
            <span onClick={() => setTemp([])} style={{ color: "#94a3b8", cursor: "pointer", fontWeight: 700 }}>Desmarcar</span>
          </div>
          <div style={{ maxHeight: 190, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
            {filtradas.length === 0 && <div style={{ fontSize: 12, color: "#94a3b8", padding: 4 }}>Sin valores</div>}
            {filtradas.map((o) => (
              <label key={o} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, cursor: "pointer", padding: "3px 2px" }}>
                <input type="checkbox" checked={temp.includes(o)} onChange={() => toggle(o)} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o === "" ? "(vacío)" : String(o)}</span>
              </label>
            ))}
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
            <button onClick={() => { onChange([]); setAbierto(false); }} style={btnLimpiar}>Limpiar</button>
            <button onClick={() => { onChange(temp); setAbierto(false); }} style={btnAplicar}>Aplicar</button>
          </div>
        </PanelFlotante>
      )}
    </span>
  );
};

// Popover de filtro por ID exacto (fila_id no tiene lista de valores únicos)
const IdFilterPopover = ({ valor, onChange }) => {
  const [abierto, setAbierto] = useState(false);
  const [temp, setTemp] = useState(valor);
  const ref = useRef(null);

  useEffect(() => { if (abierto) setTemp(valor); }, [abierto, valor]);
  const cerrar = useCallback(() => setAbierto(false), []);

  return (
    <span ref={ref} style={{ position: "relative", display: "inline-block", marginLeft: 5 }}>
      <button
        onClick={(e) => { e.stopPropagation(); setAbierto((a) => !a); }}
        title="Filtrar por ID"
        style={{
          border: "none", background: valor ? "#4338ca" : "rgba(255,255,255,0.15)",
          color: valor ? "#fff" : "#cbd5e1", borderRadius: 5, cursor: "pointer",
          fontSize: 10, padding: "2px 6px", lineHeight: 1.4, fontWeight: 700,
        }}
      >▾</button>
      {abierto && (
        <PanelFlotante anchorRef={ref} onCerrar={cerrar} style={{
          background: "#fff",
          border: "1.5px solid #e2e8f0", borderRadius: 12, boxShadow: "0 16px 36px rgba(15,23,42,0.25)",
          width: 170, padding: 10,
        }}>
          <input
            type="number" value={temp} onChange={(e) => setTemp(e.target.value)} placeholder="ID exacto…"
            style={{ width: "100%", padding: "6px 8px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12.5, marginBottom: 8, boxSizing: "border-box", outline: "none" }}
          />
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => { setTemp(""); onChange(""); setAbierto(false); }} style={btnLimpiar}>Limpiar</button>
            <button onClick={() => { onChange(temp); setAbierto(false); }} style={btnAplicar}>Aplicar</button>
          </div>
        </PanelFlotante>
      )}
    </span>
  );
};

const btnLimpiar = { flex: 1, padding: "6px 0", borderRadius: 8, border: "1px solid #e2e8f0", background: "#f8fafc", color: "#475569", fontWeight: 700, fontSize: 12, cursor: "pointer" };
const btnAplicar = { flex: 1, padding: "6px 0", borderRadius: 8, border: "none", background: "#4338ca", color: "#fff", fontWeight: 700, fontSize: 12, cursor: "pointer" };

const EVENTO_INFO = {
  ALTA: { emoji: "🟢", bg: "#dcfce7", color: "#166534" },
  BAJA: { emoji: "🔴", bg: "#fee2e2", color: "#991b1b" },
  MODIFICACION: { emoji: "🟡", bg: "#fef9c3", color: "#854d0e" },
};

const COLUMNAS = [
  { key: "fecha_hora", label: "Fecha y hora", sortable: true },
  { key: "tabla_destino", label: "Tabla", sortable: true, filtro: "lista" },
  { key: "tipo_evento", label: "Evento", sortable: true, filtro: "lista" },
  { key: "fila_id", label: "ID", sortable: true, filtro: "id" },
  { key: "identificador_snapshot", label: "Identificador", sortable: true, filtro: "lista" },
  { key: "campo", label: "Campo", sortable: true, filtro: "lista" },
  { key: "valor_anterior", label: "Valor anterior", sortable: false },
  { key: "valor_nuevo", label: "Valor nuevo", sortable: false },
  { key: "editor_email", label: "Editor", sortable: true, filtro: "lista" },
];

const FILTROS_VACIOS = { tabla_destino: [], tipo_evento: [], campo: [], editor_email: [], identificador_snapshot: [] };

const formatCelda = (v) => {
  if (v === undefined || v === null || v === "") return "";
  const s = String(v);
  return s.length > 60 ? `${s.slice(0, 60)}…` : s;
};

// Etiqueta legible para una clave de snapshot (ALTA/BAJA): "id_alumno" -> "Id alumno"
const etiquetaCampo = (k) => {
  const s = String(k).replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
};

// "558" + {"558":"SF32.6D"} -> "SF32.6D #558"; si no hay nombre, solo "#558"
const formatPiso = (idPiso, pisosMap) => {
  if (idPiso === null || idPiso === undefined || idPiso === "") return null;
  const abrev = (pisosMap || {})[String(idPiso)];
  return abrev ? `${abrev} #${idPiso}` : `#${idPiso}`;
};

// Valor legible de un campo del snapshot: null -> "—", booleans conocidos, id_piso resuelto, resto tal cual
const valorLegible = (k, v, pisosMap) => {
  if (v === null || v === undefined || v === "") return <span style={{ color: "#cbd5e1", fontStyle: "italic" }}>—</span>;
  if (k === "activo") return v === 1 || v === true ? "✅ Activo" : "❌ Inactivo";
  if (k === "id_piso") return formatPiso(v, pisosMap);
  return String(v);
};

const Historial = () => {
  const navigate = useNavigate();

  const [filtros, setFiltros] = useState(FILTROS_VACIOS);
  const [filaId, setFilaId] = useState("");
  const [q, setQ] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [orden, setOrden] = useState("fecha_hora");
  const [dir, setDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pisosMap, setPisosMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [opciones, setOpciones] = useState({ tabla_destino: [], tipo_evento: [], campo: [], editor_email: [], identificador_snapshot: [] });
  const [modal, setModal] = useState(null); // { titulo, contenido }

  const cargarOpciones = useCallback(() => {
    const params = new URLSearchParams();
    if (filtros.tabla_destino.length) params.set("tabla_destino", filtros.tabla_destino.join(","));
    axios.get(`/api/import/historial/opciones?${params.toString()}`)
      .then((r) => setOpciones(r.data || {}))
      .catch(() => {});
  }, [filtros.tabla_destino]);

  useEffect(() => { cargarOpciones(); }, [cargarOpciones]);

  const cargar = useCallback(() => {
    setLoading(true);
    setError("");
    const params = new URLSearchParams();
    Object.entries(filtros).forEach(([k, v]) => { if (v.length) params.set(k, v.join(",")); });
    if (filaId) params.set("fila_id", filaId);
    if (q.trim()) params.set("q", q.trim());
    if (desde) params.set("desde", desde);
    if (hasta) params.set("hasta", hasta);
    params.set("orden", orden);
    params.set("dir", dir);
    params.set("page", page);
    params.set("per_page", perPage);
    axios.get(`/api/import/historial?${params.toString()}`)
      .then((r) => { setRows(r.data.rows || []); setTotal(r.data.total || 0); setPisosMap(r.data.pisos_map || {}); })
      .catch(() => setError("❌ No se pudo cargar el historial."))
      .finally(() => setLoading(false));
  }, [filtros, filaId, q, desde, hasta, orden, dir, page, perPage]);

  useEffect(() => { cargar(); }, [cargar]);

  // Cualquier cambio de filtro/orden vuelve a la página 1
  const actualizarFiltro = (campo, valor) => { setFiltros((f) => ({ ...f, [campo]: valor })); setPage(1); };
  const cambiarOrden = (col) => {
    if (!COLUMNAS.find((c) => c.key === col)?.sortable) return;
    if (orden === col) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setOrden(col); setDir("desc"); }
    setPage(1);
  };

  const limpiarTodo = () => {
    setFiltros(FILTROS_VACIOS); setFilaId(""); setQ(""); setDesde(""); setHasta("");
    setOrden("fecha_hora"); setDir("desc"); setPage(1);
  };

  const hayFiltrosActivos = useMemo(
    () => Object.values(filtros).some((v) => v.length) || filaId || q.trim() || desde || hasta,
    [filtros, filaId, q, desde, hasta]
  );

  const verValor = (titulo, valor, campo) => {
    if (valor === undefined || valor === null || valor === "") { setModal({ titulo, tipo: "vacio" }); return; }
    if (campo === "id_piso") {
      setModal({ titulo, tipo: "texto", contenido: formatPiso(valor, pisosMap) || String(valor) });
      return;
    }
    try {
      const parsed = JSON.parse(valor);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        setModal({ titulo, tipo: "objeto", datos: parsed });
        return;
      }
      setModal({ titulo, tipo: "texto", contenido: String(parsed) });
    } catch (_) {
      setModal({ titulo, tipo: "texto", contenido: String(valor) });
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return (
    <div style={{ minHeight: "100vh", background: "#f0f4f8" }}>
      <div style={{ background: "linear-gradient(135deg,#1e293b,#0f172a)", color: "#fff", padding: "14px 16px 18px", display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={() => navigate("/superadmin/importar")} style={{ background: "rgba(255,255,255,0.12)", border: "none", borderRadius: 10, color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer", padding: "8px 14px" }}>
          ← Volver
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>Importar Datos</div>
          <div style={{ fontSize: 18, fontWeight: 800 }}>🕘 Historial de cambios</div>
        </div>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", textAlign: "right" }}>{total} registros</div>
      </div>

      {/* Barra de fecha + búsqueda libre */}
      <div style={{ display: "flex", gap: 10, padding: "12px 14px", flexWrap: "wrap", alignItems: "center", background: "#fff", borderBottom: "1px solid #e2e8f0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <label style={{ fontSize: 12.5, fontWeight: 700, color: "#475569" }}>📅 Desde</label>
          <input type="date" value={desde} max={hasta || undefined} onChange={(e) => { setDesde(e.target.value); setPage(1); }}
            style={{ padding: "7px 9px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 13 }} />
          <label style={{ fontSize: 12.5, fontWeight: 700, color: "#475569" }}>Hasta</label>
          <input type="date" value={hasta} min={desde || undefined} onChange={(e) => { setHasta(e.target.value); setPage(1); }}
            style={{ padding: "7px 9px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 13 }} />
          {desde && (
            <button onClick={() => { setHasta(desde); setPage(1); }} title="Ver solo el día 'Desde'" style={{ fontSize: 11.5, fontWeight: 700, color: "#4338ca", background: "#eef2ff", border: "1px solid #c7d2fe", borderRadius: 8, padding: "6px 8px", cursor: "pointer" }}>
              Solo ese día
            </button>
          )}
        </div>
        <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="🔍 Buscar (identificador, editor, campo, ID)…"
          style={{ flex: 1, minWidth: 220, padding: "8px 12px", borderRadius: 10, border: "1.5px solid #e2e8f0", fontSize: 13.5, outline: "none" }} />
        <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
          style={{ padding: "8px 10px", borderRadius: 10, border: "1.5px solid #e2e8f0", fontSize: 13, background: "#fff", fontWeight: 600, color: "#475569" }}>
          {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n} / página</option>)}
        </select>
        {hayFiltrosActivos && (
          <button onClick={limpiarTodo} style={{ padding: "8px 14px", borderRadius: 10, border: "1.5px solid #dc2626", background: "#fee2e2", color: "#991b1b", fontWeight: 700, fontSize: 12.5, cursor: "pointer" }}>
            ✕ Quitar todos los filtros
          </button>
        )}
      </div>

      {/* Tabla */}
      <div style={{ padding: "12px 14px" }}>
        {error && <div style={{ marginBottom: 10, color: "#dc2626", fontSize: 13 }}>{error}</div>}
        <div style={{ overflowX: "auto", background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0", boxShadow: "0 2px 10px rgba(15,23,42,0.05)" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
            <thead>
              <tr>
                {COLUMNAS.map((c) => (
                  <th key={c.key} style={th}>
                    <div style={{ display: "flex", alignItems: "center", gap: 2, whiteSpace: "nowrap" }}>
                      <span
                        onClick={() => cambiarOrden(c.key)}
                        style={{ cursor: c.sortable ? "pointer" : "default", userSelect: "none" }}
                      >
                        {c.label}
                        {c.sortable && orden === c.key && (dir === "asc" ? " ▲" : " ▼")}
                      </span>
                      {c.filtro === "lista" && (
                        <ColumnFilterPopover
                          opciones={opciones[c.key] || []}
                          seleccionados={filtros[c.key] || []}
                          onChange={(v) => actualizarFiltro(c.key, v)}
                        />
                      )}
                      {c.filtro === "id" && (
                        <IdFilterPopover valor={filaId} onChange={(v) => { setFilaId(v); setPage(1); }} />
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={COLUMNAS.length} style={{ textAlign: "center", padding: 40, color: "#64748b" }}>⏳ Cargando…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={COLUMNAS.length} style={{ textAlign: "center", padding: 40, color: "#94a3b8" }}>Sin resultados para estos filtros.</td></tr>
              ) : (
                rows.map((r, i) => {
                  const ev = EVENTO_INFO[r.tipo_evento] || {};
                  return (
                    <tr key={r.id} style={{ background: i % 2 ? "#fff" : "#fcfdfe" }}>
                      <td style={td}>{r.fecha_hora ? new Date(r.fecha_hora).toLocaleString("es-ES") : ""}</td>
                      <td style={td}>{r.tabla_destino}</td>
                      <td style={td}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 700, fontSize: 12, background: ev.bg, color: ev.color, borderRadius: 10, padding: "3px 9px" }}>
                          {ev.emoji} {r.tipo_evento}
                        </span>
                      </td>
                      <td style={td}>{r.fila_id}</td>
                      <td style={{ ...td, fontWeight: 700, color: "#1e293b" }}>{r.identificador_snapshot}</td>
                      <td style={td}>{r.campo || "—"}</td>
                      <td style={td}>
                        {r.valor_anterior
                          ? (r.tipo_evento === "ALTA" || r.tipo_evento === "BAJA"
                              ? <span onClick={() => verValor("Valor anterior", r.valor_anterior, r.campo)} style={cellLink}>
                                  {r.campo === "id_piso" ? formatPiso(r.valor_anterior, pisosMap) : formatCelda(r.valor_anterior)}
                                </span>
                              : <span>{r.campo === "id_piso" ? formatPiso(r.valor_anterior, pisosMap) : formatCelda(r.valor_anterior)}</span>)
                          : <span style={{ color: "#cbd5e1" }}>—</span>}
                      </td>
                      <td style={td}>
                        {r.valor_nuevo
                          ? (r.tipo_evento === "ALTA" || r.tipo_evento === "BAJA"
                              ? <span onClick={() => verValor("Valor nuevo", r.valor_nuevo, r.campo)} style={cellLink}>
                                  {r.campo === "id_piso" ? formatPiso(r.valor_nuevo, pisosMap) : formatCelda(r.valor_nuevo)}
                                </span>
                              : <span>{r.campo === "id_piso" ? formatPiso(r.valor_nuevo, pisosMap) : formatCelda(r.valor_nuevo)}</span>)
                          : <span style={{ color: "#cbd5e1" }}>—</span>}
                      </td>
                      <td style={td}>{r.editor_email}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 14 }}>
          <button onClick={() => setPage(1)} disabled={page <= 1} style={pagBtn(page <= 1)}>«</button>
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} style={pagBtn(page <= 1)}>‹ Anterior</button>
          <span style={{ fontSize: 13, color: "#475569", fontWeight: 700 }}>Página {page} de {totalPages}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={pagBtn(page >= totalPages)}>Siguiente ›</button>
          <button onClick={() => setPage(totalPages)} disabled={page >= totalPages} style={pagBtn(page >= totalPages)}>»</button>
        </div>
      </div>

      {/* Modal de valor completo */}
      {modal && (
        <div onClick={() => setModal(null)} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 620, maxHeight: "80vh", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ padding: "14px 18px", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 10 }}>
              <strong style={{ flex: 1, fontSize: 15 }}>{modal.titulo}</strong>
              <button onClick={() => setModal(null)} style={{ border: "none", background: "#f1f5f9", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontWeight: 700 }}>✕</button>
            </div>
            <div style={{ padding: "6px 18px 16px", overflow: "auto" }}>
              {modal.tipo === "vacio" ? (
                <div style={{ color: "#94a3b8", fontStyle: "italic", padding: "10px 0" }}>(vacío)</div>
              ) : modal.tipo === "objeto" ? (
                Object.entries(modal.datos).map(([k, v]) => (
                  <div key={k} style={{ display: "flex", gap: 14, padding: "10px 0", borderBottom: "1px solid #f1f5f9" }}>
                    <div style={{ width: 150, flexShrink: 0, fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.3 }}>{etiquetaCampo(k)}</div>
                    <div style={{ flex: 1, fontSize: 14.5, color: "#1e293b", wordBreak: "break-word" }}>{valorLegible(k, v, pisosMap)}</div>
                  </div>
                ))
              ) : (
                <div style={{ fontSize: 14.5, color: "#1e293b", whiteSpace: "pre-wrap", padding: "10px 0" }}>{modal.contenido}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const th = { padding: "9px 12px", fontSize: 11.5, fontWeight: 700, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: 0.4, whiteSpace: "nowrap", textAlign: "left", background: "#1e293b" };
const td = { padding: "8px 12px", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap", color: "#334155", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" };
const cellLink = { color: "#4338ca", cursor: "pointer", textDecoration: "underline", textDecorationStyle: "dotted" };
const pagBtn = (disabled) => ({ padding: "7px 14px", borderRadius: 10, border: "1.5px solid #e2e8f0", background: disabled ? "#f8fafc" : "#fff", color: disabled ? "#cbd5e1" : "#334155", fontWeight: 700, fontSize: 13, cursor: disabled ? "default" : "pointer" });

export default Historial;
